import {
  Activity,
  Bot,
  ChartLine,
  Clapperboard,
  ClipboardCheck,
  Coins,
  CreditCard,
  FileQuestion,
  Flag,
  Hourglass,
  Landmark,
  LifeBuoy,
  List,
  Megaphone,
  ScanFace,
  ShieldAlert,
  ShieldCheck,
  Store,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import { PERMISSIONS, type Permission } from "@/api/permissions";
import type { ReviewQueueSummary } from "@/api/types";

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * Required — every page must name the permission that opens it. Roles like
   * `marketing` holds only a couple of permissions (metrics + the Creator
   * Program), so an ungated page would be visible to them by default; making
   * this mandatory turns that into a compile error instead.
   */
  permission: Permission;
  /**
   * Live count for the sidebar badge, read from GET /admin/review-queues/
   * summary (polled by SidebarNav). Absent → no badge.
   */
  badge?: (summary: ReviewQueueSummary) => number | null;
  /** Extra ⌘K search terms. The label and the group name always match. */
  keywords?: string[];
  /**
   * "You are here" test, when the default — this path or a child of it —
   * is wrong. Needed where two entries share a prefix (/users vs
   * /users/all) or where one entry owns a sibling route (/crypto/deposits
   * also owns /crypto/withdrawals).
   */
  match?: (pathname: string) => boolean;
}

export interface NavSection {
  /**
   * Stable key. The collapse preference is persisted under it
   * (`lib/nav-collapse.ts`) — never rename one, or every admin's collapsed
   * group silently springs open.
   */
  id: string;
  /**
   * The group header, uppercased by the sidebar. `null` = the pinned block:
   * no header, no chevron, never collapsible.
   */
  label: string | null;
  items: NavItem[];
  /** Folded on a fresh browser. */
  defaultCollapsed?: boolean;
}

/**
 * The sidebar. Items render only when the admin holds the permission —
 * this single list is what replaces the separate superadmin and
 * customer-support Retool apps.
 *
 * Two levels: a pinned block with no header, then labeled groups that
 * collapse (state in `lib/nav-collapse.ts`). Section order, and item order
 * inside a section, decide each role's landing page through the derived
 * NAV_ITEMS below — so Metrics and Users stay at the top of the pinned
 * block. See HomeRedirect.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: "pinned",
    label: null,
    items: [
      {
        label: "Metrics",
        to: "/metrics",
        icon: ChartLine,
        permission: PERMISSIONS.METRICS_READ,
        keywords: ["charts", "kpi", "analytics", "dashboard"],
      },
      {
        label: "Users",
        to: "/users",
        icon: Users,
        permission: PERMISSIONS.USERS_READ,
        keywords: ["customer", "search", "lookup", "all users"],
      },
      {
        // Is anything amiss right now: every rail's last hour from Datadog
        // logs. Third in the pinned block so the landing pages stay Metrics
        // and Users (see HomeRedirect).
        label: "Live",
        to: "/live",
        icon: Activity,
        permission: PERMISSIONS.LIVE_HEALTH_READ,
        keywords: ["health", "status", "outage", "errors", "rails", "datadog"],
      },
    ],
  },
  {
    id: "queues",
    label: "Queues",
    items: [
      {
        label: "Reviews",
        to: "/reviews",
        icon: ClipboardCheck,
        permission: PERMISSIONS.CARD_VERIFICATION_REVIEW,
        keywords: ["bank statement", "nationality", "card"],
        badge: (summary) => {
          const parts = [
            summary.fraudScoring,
            summary.sardineNationality,
            summary.bankStatements,
          ].filter((stat) => stat !== undefined);
          return parts.length === 0
            ? null
            : parts.reduce((sum, stat) => sum + stat.count, 0);
        },
      },
      {
        label: "KYC",
        to: "/kyc",
        icon: ScanFace,
        permission: PERMISSIONS.KYC_REVIEW,
        keywords: ["identity", "aiprise", "verification", "passport"],
        badge: (summary) => summary.kyc?.count ?? null,
      },
      {
        // Stables compliance requests (RFIs) across every customer: what the
        // banking partner is waiting on before it releases a held deposit or
        // verification level. The badge counts requests waiting on the
        // customer; ones under review at Stables are listed without counting.
        label: "Stables RFIs",
        to: "/stables-rfis",
        icon: FileQuestion,
        permission: PERMISSIONS.USERS_READ,
        keywords: [
          "compliance",
          "rfi",
          "request for information",
          "aud",
          "held deposit",
        ],
        badge: (summary) => summary.stablesRfis?.count ?? null,
      },
      {
        label: "Crypto",
        to: "/crypto/deposits",
        icon: Coins,
        // Deposits is the landing tab, so this is the read that opens the
        // page. The Withdrawals tab carries its own gate (see CryptoPage).
        permission: PERMISSIONS.CRYPTO_DEPOSIT_READ,
        keywords: ["usdc", "usdt", "solana", "stablecoin", "deposits"],
        // Both sides are one destination — /crypto/withdrawals lights this
        // entry too, since the side is a route rather than a param.
        match: (pathname) =>
          pathname === "/crypto" || pathname.startsWith("/crypto/"),
        // One badge for both queues — the page is one destination now, and an
        // admin only needs to know there is something waiting behind it.
        badge: (summary) =>
          (summary.cryptoDeposits?.count ?? 0) +
            (summary.cryptoWithdrawals?.count ?? 0) || null,
      },
      {
        // The queues above, seen as a whole: what is waiting, how long
        // decisions take, and the Chatwoot inbox. No badge — the three
        // entries above already carry the counts.
        label: "Support load",
        to: "/support-load",
        icon: LifeBuoy,
        permission: PERMISSIONS.SUPPORT_LOAD_READ,
        keywords: ["chatwoot", "backlog", "sla", "time to resolve", "ekyc"],
      },
    ],
  },
  {
    id: "risk",
    label: "Risk",
    items: [
      {
        label: "Fraud",
        to: "/fraud",
        icon: ShieldAlert,
        permission: PERMISSIONS.FRAUD_READ,
        keywords: ["alerts", "scores", "whitelist", "blocked"],
      },
      {
        label: "Chargebacks",
        to: "/chargebacks",
        icon: CreditCard,
        permission: PERMISSIONS.FRAUD_READ,
        keywords: ["coinflow", "dispute", "card", "fraud"],
      },
      {
        label: "Merchants",
        to: "/merchants",
        icon: Store,
        permission: PERMISSIONS.MERCHANTS_READ,
        keywords: ["store", "blocklist", "collusion"],
      },
    ],
  },
  {
    id: "money-ops",
    label: "Money ops",
    items: [
      {
        label: "Balances",
        to: "/balances",
        icon: Landmark,
        permission: PERMISSIONS.LOOKUPS_READ,
        keywords: ["fx", "float", "prefund", "batch", "inventory", "rates"],
      },
      {
        label: "Stale payments",
        to: "/rails/stale-transactions",
        icon: Hourglass,
        permission: PERMISSIONS.LOOKUPS_READ,
        keywords: ["reconcile", "rails", "pending", "stuck"],
      },
      {
        label: "Privy wallet",
        to: "/master-wallet",
        icon: Wallet,
        permission: PERMISSIONS.MASTER_WALLET_READ,
        keywords: ["master wallet", "treasury", "sweep", "minimum"],
      },
    ],
  },
  {
    id: "growth",
    label: "Growth",
    items: [
      {
        label: "Creator Program",
        to: "/creator-program",
        icon: Clapperboard,
        permission: PERMISSIONS.CREATOR_REWARDS_READ,
        keywords: ["videos", "creators", "credits", "rewards"],
      },
      {
        label: "Announcements",
        to: "/announcements",
        icon: Megaphone,
        permission: PERMISSIONS.ANNOUNCEMENTS_READ,
        keywords: ["home cards", "banner", "inbox", "push", "notification"],
      },
    ],
  },
  {
    id: "system",
    label: "System",
    // Config and tooling nobody opens daily — folded on a fresh browser.
    defaultCollapsed: true,
    items: [
      {
        label: "Feature Flags",
        to: "/feature-flags",
        icon: Flag,
        permission: PERMISSIONS.FEATURE_FLAGS_READ,
        keywords: ["toggle", "rollout"],
      },
      {
        label: "Admin limits",
        to: "/admin-limits",
        icon: ShieldCheck,
        permission: PERMISSIONS.ADMIN_SPENDING_READ,
        keywords: ["spending", "caps", "overrides", "audit"],
      },
      {
        label: "AI Chatbot",
        to: "/chatbot",
        icon: Bot,
        permission: PERMISSIONS.AI_CHATBOT,
        keywords: ["ask", "sql", "query", "logs", "posthog"],
      },
    ],
  },
];

export interface NavPage extends NavItem {
  /** The group this page sits under; null for the pinned block. */
  section: string | null;
}

/**
 * The same pages, flat and in sidebar order, each carrying its group name.
 * `HomeRedirect` takes the first entry the role can open — so the pinned
 * block must stay first — and the ⌘K palette lists and searches it.
 */
export const NAV_ITEMS: NavPage[] = NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({ ...item, section: section.label })),
);

/**
 * Reachable from ⌘K but deliberately not in the sidebar — each is a
 * drill-down from a page that already has an entry, and the sidebar is the
 * thing we are trying to keep short.
 *
 * `/metrics/rails/:provider` is absent on purpose: it has no index route,
 * only a per-rail one, so there is nothing to link to.
 */
export const PALETTE_ONLY_ITEMS: NavPage[] = [
  {
    label: "All users",
    to: "/users/all",
    icon: List,
    permission: PERMISSIONS.USERS_READ,
    section: "Users",
    keywords: ["list", "table", "everyone", "browse", "export"],
  },
  {
    label: "OTP insights",
    to: "/metrics/otp",
    icon: ChartLine,
    permission: PERMISSIONS.METRICS_READ,
    section: "Metrics",
    keywords: ["otp", "phone", "sms", "verification", "signup", "funnel"],
  },
  {
    label: "Crypto withdrawals",
    to: "/crypto/withdrawals",
    icon: Coins,
    permission: PERMISSIONS.CRYPTO_WITHDRAWAL_READ,
    section: "Queues",
    keywords: ["usdc", "usdt", "stablecoin", "chainalysis", "held"],
  },
];

/** Everything the ⌘K palette can jump to. */
export const PALETTE_PAGES: NavPage[] = [...NAV_ITEMS, ...PALETTE_ONLY_ITEMS];

/**
 * Is `pathname` inside this nav entry? Defaults to a prefix match, so
 * /users/:userId keeps Users lit and /merchants/view keeps Merchants lit;
 * entries that share a prefix with a sibling carry their own `match`.
 */
export const isNavItemActive = (item: NavItem, pathname: string): boolean =>
  item.match
    ? item.match(pathname)
    : pathname === item.to || pathname.startsWith(`${item.to}/`);

/** The section holding `pathname`, or null when nothing matches. */
export const sectionIdForPath = (pathname: string): string | null =>
  NAV_SECTIONS.find((section) =>
    section.items.some((item) => isNavItemActive(item, pathname)),
  )?.id ?? null;

/** Groups that start folded before the admin has expressed a preference. */
export const DEFAULT_COLLAPSED_SECTIONS: string[] = NAV_SECTIONS.filter(
  (section) => section.defaultCollapsed,
).map((section) => section.id);

/**
 * At or below this many visible pages the sidebar drops all group chrome and
 * renders one flat list — headers and chevrons around `marketing`'s two
 * entries are noise, not structure.
 */
export const NAV_FLATTEN_THRESHOLD = 4;
